import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { ThemeProvider } from 'styled-components';
import { useIntl } from 'react-intl-next';
import { getHeadless, getServiceDeskVersion } from 'state/selectors/env';
import { getHelpCenterTitleColor, getThemeColor } from 'state/selectors/help-center';
import { lightenColor, readableTextColor } from 'view/styles/colors';
import Banner from '@atlaskit/banner';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { SpotlightManager } from '@atlaskit/onboarding';
import { AnalyticsContext } from '@atlassian/analytics-web-react';
import { FeedbackCollector } from '@atlassian/help-center-common-component/feedback-collector';
import { FlagGroup } from '@atlassian/help-center-common-component/flag-group';
import { MarkAppMounted } from '@atlassian/help-center-common-component/mark-app-mounted';
import { BrowserMetricsScreenTransitionStatusProvider } from '@atlassian/help-center-common-util/analytics/browsermetrics-screen-transition-status-context';
import { isBrowserUnsupported } from '@atlassian/help-center-common-util/unsupported-browsers';
import messages from './messages';
import ResourceRouter from './resource-router';

export interface Props {
    headerless: boolean;
    themeColor: string;
    titleColor: string;
    serviceDeskVersion: string;
}

const BUILD_KEY = window.JSD_CUSTOMER_PORTAL_BUILD_VERSION?.buildKey || 'local';

export const App = (props: Props) => {
    const { themeColor, serviceDeskVersion, titleColor } = props;
    const readableTextColorFromTheme = readableTextColor(themeColor);
    const lightColorFromTheme = lightenColor(themeColor);
    const { formatMessage } = useIntl();

    const theme = {
        titleColor,
        readableTextColor: readableTextColorFromTheme,
        color: themeColor,
        lightColor: lightColorFromTheme,
    };

    const analyticData = {
        version: serviceDeskVersion,
        application: 'jira-servicedesk',
        edition: 'standard',
        buildKey: BUILD_KEY,
    };

    return (
        <SpotlightManager blanketIsTinted={false}>
            <AnalyticsContext attributes={analyticData}>
                <ThemeProvider theme={theme}>
                    <BrowserMetricsScreenTransitionStatusProvider>
                        {isBrowserUnsupported() && (
                            <Banner appearance="warning" icon={<WarningIcon label="" secondaryColor="inherit" />}>
                                {formatMessage(messages.unsupportedBrowser)}
                            </Banner>
                        )}
                        <ResourceRouter />
                        <FlagGroup />
                        <FeedbackCollector />
                        <MarkAppMounted />
                    </BrowserMetricsScreenTransitionStatusProvider>
                </ThemeProvider>
            </AnalyticsContext>
        </SpotlightManager>
    );
};

const selector = createSelector(
    getHeadless,
    getThemeColor,
    getServiceDeskVersion,
    getHelpCenterTitleColor,
    (headerless, themeColor, serviceDeskVersion, titleColor) => ({
        headerless,
        themeColor,
        serviceDeskVersion,
        titleColor,
    })
);

export default connect(selector)(App);
